import {
  ConsoleInstrumentation,
  createReactRouterV5Options,
  ErrorsInstrumentation,
  initializeFaro,
  ReactIntegration,
  type Instrumentation,
  type ReactRouterHistory,
  ViewInstrumentation,
  WebVitalsInstrumentation,
} from '@grafana/faro-react';
import {
  CSPInstrumentation,
  PerformanceInstrumentation,
  SessionInstrumentation,
  UserActionInstrumentation,
} from '@grafana/faro-web-sdk';
import { Route } from 'react-router-dom';
import { getEnvironment } from '@minecraft.environment';
import { PRODUCTION } from '../Auth/constants';
import { parseJwt } from '../Auth/utils/token';
import { getAccessToken } from '../Auth/utils/authStorage';
import { RouterHistory } from '../Router';

const INSTRUMENTATION_MAP: Record<string, () => Instrumentation> = {
  errors: () => new ErrorsInstrumentation(),
  console: () => new ConsoleInstrumentation(),
  'web-vitals': () => new WebVitalsInstrumentation(),
  session: () => new SessionInstrumentation(),
  view: () => new ViewInstrumentation(),
  performance: () => new PerformanceInstrumentation(),
  'user-actions': () => new UserActionInstrumentation(),
  csp: () => new CSPInstrumentation(),
};

const DEFAULT_INSTRUMENTATIONS = 'errors';

function getEnabledInstrumentations(configValue: string | undefined): Instrumentation[] {
  const raw = configValue?.trim() || DEFAULT_INSTRUMENTATIONS;
  const keys = raw
    .split(',')
    .map((k) => k.trim())
    .filter(Boolean);

  return keys.reduce<Instrumentation[]>((acc, key) => {
    const factory = INSTRUMENTATION_MAP[key];
    if (factory) {
      acc.push(factory());
    }
    return acc;
  }, []);
}

const buildFaroUser = () => {
  const tokenInfo = parseJwt(getAccessToken());

  if (!tokenInfo) {
    return {
      id: 'unauthenticated',
      attributes: {
        windowHt: (typeof window !== 'undefined' ? window.innerHeight : 0).toString(),
        windowWidth: (typeof window !== 'undefined' ? window.innerWidth : 0).toString(),
      },
    };
  }

  return {
    id: tokenInfo?.id?.toString(),
    email: tokenInfo?.email?.toString(),
    attributes: {
      accountId: tokenInfo?.id?.toString(),
      role: tokenInfo?.role?.code?.toString(),
      artistId: tokenInfo?.artistId?.toString(),
      organizationId: tokenInfo?.organizationId?.toString(),
      windowHt: (typeof window !== 'undefined' ? window.innerHeight : 0).toString(),
      windowWidth: (typeof window !== 'undefined' ? window.innerWidth : 0).toString(),
    },
  };
};

export const initFaro = ({ history }: { history: RouterHistory }) => {
  const environment = getEnvironment();

  if (environment.ENV === PRODUCTION || !environment.FARO_ENDPOINT) return;

  const instrumentations = getEnabledInstrumentations(environment.FARO_INSTRUMENTATIONS);

  const keys = (environment.FARO_INSTRUMENTATIONS?.trim() || DEFAULT_INSTRUMENTATIONS).split(',').map((k) => k.trim());

  if (keys.includes('react-router')) {
    instrumentations.push(
      new ReactIntegration({
        router: createReactRouterV5Options({ history: history as ReactRouterHistory, Route }),
      }) as unknown as Instrumentation
    );
  }

  initializeFaro({
    url: environment.FARO_ENDPOINT,
    app: {
      name: environment.AUDIENCE,
      version: environment.B21_RELEASE,
      environment: environment.ENV,
    },
    instrumentations,
    beforeSend: (item) => {
      const userMeta = buildFaroUser();

      return {
        ...item,
        meta: {
          ...(item.meta || {}),
          user: userMeta,
        },
      };
    },
  });
};
